import { to } from 'await-to-js';
import { Client } from 'contensis-management-api';
import { User } from 'contensis-management-api/lib/models';
import { ClientCredentials } from '@/types/ClientCredentials.types';
import getManagementApiClient from '../ContensisManagementAPI';
import BrowserRedirect from './BrowserRedirect';
import CredentialStore from './CredentialStore';
import OidcSignOn from './SingleSignOn';
class ContensisUserManager {
  bearerToken?: string;

  // a new CredentialStore will pre-load persisted credentials
  credentials = new CredentialStore();

  // track any error throughout any operation
  error?: Error;

  // the retrieved user details (if logged in)
  user?: User | null;

  private client?: Client;
  private isStorybook = typeof window.__STORYBOOK_ADDONS !== 'undefined';
  private sso?: OidcSignOn;

  // constructor() {}

  static HasSecurityTokenQueryString = () => {
    const params = new URLSearchParams(window.location.search);
    const securityToken =
      params.get('securitytoken') || params.get('securityToken');
    return securityToken;
  };

  static RemoveSecurityTokenQueryString = () => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('securitytoken') || params.has('securityToken')) {
      params.delete('securitytoken');
      params.delete('securityToken');
      window.history.replaceState(
        null,
        '',
        `${window.location.pathname}${params.toString() ? `?${params}` : ''}`
      );
    }
  };

  InitLogin = async () => {
    // Check for securityToken in query string, exchange this for a refreshToken
    // via a classic REST endpoing, save refreshToken in a cookie
    await this.SecurityTokenCheck();

    const { credentials } = this;
    if (credentials.refreshToken) {
      await this.Login();
    }
    return this;
  };

  GetClientCredentials = () =>
    ({
      bearerToken: this.client?.bearerToken,
      refreshToken: this.client?.refreshToken,
      bearerTokenExpiryDate: this.client?.bearerTokenExpiryDate.toUTCString(),
      contensisClassicToken: (this.client as any)?.contensisClassicToken,
      refreshTokenExpiryDate: this.client?.refreshTokenExpiryDate
        ? this.client.refreshTokenExpiryDate?.toUTCString()
        : 0,
    }) as ClientCredentials;

  Login = async (
    credentials:
      | { sso: true }
      | { username: string; password: string }
      | ClientCredentials = this.credentials,
    type: 'popup' | 'redirect' = 'redirect'
  ) => {
    if (credentials && 'sso' in credentials) {
      const oidc = await this.InitOidcSignOn();
      await oidc.SignoutRedirect();
      if (type === 'popup') {
        const [err, user] = (await oidc.SigninPopup()) || [null, undefined];
        if (err) this.error = err;
        console.info(err, user);
        // We may have set a cookie in SignInCallback component, read it back
        this.credentials.refresh();
        if (user && this.credentials.refreshToken) {
          // Success
          this.client = getManagementApiClient(this.credentials);
          await this.RefreshBearerToken();
        }
      } else {
        const [err] = await oidc.SigninRedirect();
        if (err) this.error = err;
        return this;
      }
    } else if (credentials) {
      this.client = getManagementApiClient(credentials);
      await this.RefreshBearerToken();
    } else {
      this.error = new Error('No credentials supplied');
    }

    if (this.bearerToken) {
      this.credentials.persistCredential(this.GetClientCredentials());
      this.user = await this.GetUserDetails();
      ContensisUserManager.RemoveSecurityTokenQueryString();
    } else {
      this.error = this.error || new Error('Unable to log you in');
    }

    
    return this;
  };

  SsoPopupCallback = async () => {
    if (!this.sso) await this.InitOidcSignOn();
    const contensisClassicToken =
      ContensisUserManager.HasSecurityTokenQueryString();
    if (contensisClassicToken) {
      this.credentials.persistCredential({
        contensisClassicToken,
      });
      await this.SecurityTokenCheck();
    }
    this.sso?.userManager.signinPopupCallback();
  };

  Logout = (redirectPath?: string) => {
    // TODO: apply a timeout so components don't get stuck here
    // and handle the failure in the ui
    this.credentials.clearPersisted();
    if (this.sso) {
      this.sso.RemoveCachedKeys();
      this.sso.SignoutRedirect(redirectPath);
    } else {
      if (redirectPath) BrowserRedirect.ToPath(redirectPath);
      else BrowserRedirect.SignInPage();
    }
  };

  RefreshBearerToken = async () => {
    if (this.client) {
      // Ensure the client has requested a bearer token
      const [error, clientBearerToken] = await to(
        this.client.ensureBearerToken()
      );

      if (error) {
        console.error(error);
        this.error = error;
      }

      // Got a token from the current client credentials
      if (typeof clientBearerToken !== 'undefined') {
        this.bearerToken = clientBearerToken;
        this.credentials.bearerToken = clientBearerToken;
        this.credentials.bearerTokenExpiryDate =
          this.client.bearerTokenExpiryDate.toUTCString();
        this.credentials.refreshToken = this.client.refreshToken;
        this.credentials.refreshTokenExpiryDate =
          this.client.refreshTokenExpiryDate?.toUTCString();
      }
    }
  };

  /**
   *  Check for securityToken in query string, exchange this for a refreshToken
   *  via a classic REST endpoing, save refreshToken in a cookie
   */
  SecurityTokenCheck = async (): Promise<
    [Error | undefined, string | undefined]
  > => {
    const securityToken = ContensisUserManager.HasSecurityTokenQueryString();
    if (securityToken) {
      const { credentials } = this;
      const [tokenErr, refreshToken] =
        await credentials.getRefreshTokenFromSecurityToken(securityToken);
      if (tokenErr) {
        if (window.top) window.top.alert(tokenErr);
        else console.error(tokenErr);
      }
      if (refreshToken) {
        credentials.persistCredential({ refreshToken });
      }
      return [tokenErr, refreshToken];
    }
    return [undefined, undefined];
  };

  private GetUserDetails = async () => {
    if (!this.client) return null;

    const [error, user] = await to(this.client.security.users.getCurrent());
    if (error) {
      console.error(error);
      this.error = error;
      return null;
    }
    return user;
  };

  private InitOidcSignOn = async () => {
    if (!this.sso) {
      const ssoClient = (await import('./SingleSignOn'))?.default;
      this.sso = await new ssoClient().Init();
    }
    return this.sso;
  };
}

export const userManager = new ContensisUserManager();

export default ContensisUserManager;
